/** TanStack Query hooks for the admin bulk-import surface (P1 Tier-A). */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  cancelImport,
  commitImport,
  getImportErrorsLink,
  getImportJob,
  importKeys,
  uploadImport,
  type ImportErrorsLink,
  type ImportJobKind,
  type ImportJobStatus,
} from "@/features/admin/import/api";

const ACTIVE_PHASES = new Set(["queued", "dry_run_running", "committing"]);
const POLL_MS = 1500;

export function useUploadImport(): UseMutationResult<
  { job_id: string },
  Error,
  { kind: ImportJobKind; file: File }
> {
  return useMutation<{ job_id: string }, Error, { kind: ImportJobKind; file: File }>({
    mutationFn: ({ kind, file }) => uploadImport(kind, file),
  });
}

export function useImportJob(id: string | null): UseQueryResult<ImportJobStatus, Error> {
  return useQuery<ImportJobStatus, Error>({
    queryKey: importKeys.job(id ?? ""),
    queryFn: () => getImportJob(id!),
    enabled: id !== null,
    // Poll only while the worker is mid-flight; stop once terminal/awaiting input.
    refetchInterval: (query) => {
      const phase = query.state.data?.phase;
      return phase !== undefined && ACTIVE_PHASES.has(phase) ? POLL_MS : false;
    },
  });
}

export function useImportErrorsLink(
  id: string | null,
  enabled: boolean,
): UseQueryResult<ImportErrorsLink, Error> {
  return useQuery<ImportErrorsLink, Error>({
    queryKey: importKeys.errors(id ?? ""),
    queryFn: () => getImportErrorsLink(id!),
    enabled: id !== null && enabled,
    staleTime: 60_000,
  });
}

export function useCommitImport(): UseMutationResult<{ job_id: string }, Error, string> {
  const qc = useQueryClient();
  return useMutation<{ job_id: string }, Error, string>({
    mutationFn: (id) => commitImport(id),
    onSuccess: (_d, id) => qc.invalidateQueries({ queryKey: importKeys.job(id) }),
  });
}

export function useCancelImport(): UseMutationResult<
  ImportJobStatus,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation<ImportJobStatus, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => cancelImport(id, reason),
    onSuccess: (_d, { id }) => qc.invalidateQueries({ queryKey: importKeys.job(id) }),
  });
}
